import {
  Box,
  CircularProgress,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useState } from "react";
import ClaimSummary from "./ClaimSummary";
import ModuleBreakdown from "./ModuleBreakdown";
import ProfitLossDetailTable from "./ProfitLossDetailTable";
import ProfitLossFilters from "./ProfitLossFilters";
import SummaryCards from "./SummaryCards";
import { deriveDateRange } from "./plConstants";

const now = new Date();

const DEFAULT_FILTERS = {
  dateMode: "ALL",
  year: now.getFullYear(),
  month: now.getMonth() + 1,
  from: "",
  to: "",
  module: "",
  caseOfficer: "",
  claimFilter: "ALL",
  search: "",
};

const ProfitLoss = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [view, setView] = useState("GROUPED");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState([]);
  const [effective, setEffective] = useState(false);

  const [{ profitLossReport, isProfitLossLoading }, { getProfitLossReport }] = useKothar();

  const queryParams = useMemo(() => {
    const { from, to } = deriveDateRange(filters);
    const sort = sorting[0];
    return {
      from,
      to,
      module: filters.module || undefined,
      caseOfficer: filters.caseOfficer || undefined,
      claimFilter: filters.claimFilter === "ALL" ? undefined : filters.claimFilter,
      search: filters.search || undefined,
      effective,
      view,
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sortBy: sort?.id || "clientName",
      sortDirection: sort?.desc ? "DESC" : "ASC",
    };
  }, [filters, view, pagination, sorting, effective]);

  // Debounce so typing in search / case-officer doesn't fire a request per keystroke.
  const queryKey = JSON.stringify(queryParams);
  useEffect(() => {
    const timer = setTimeout(() => getProfitLossReport(queryParams), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  const resetPage = () => setPagination((p) => ({ ...p, pageIndex: 0 }));

  const onChange = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    resetPage();
  };
  const onReset = () => {
    setFilters(DEFAULT_FILTERS);
    setSorting([]);
    resetPage();
  };
  const onViewChange = (next) => {
    setView(next);
    setSorting([]);
    resetPage();
  };

  const report = profitLossReport || {};
  const page = report.page || {};
  const initialLoading = isProfitLossLoading && !profitLossReport;

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Profit / Loss
          </Typography>
          <Tooltip title="Effective counts only admission terms whose term date has passed; future terms are excluded from due / profit-loss. Other modules are unaffected.">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={effective ? "EFFECTIVE" : "TOTAL"}
              onChange={(_, v) => {
                if (v) {
                  setEffective(v === "EFFECTIVE");
                  resetPage();
                }
              }}
            >
              <ToggleButton value="TOTAL">Total</ToggleButton>
              <ToggleButton value="EFFECTIVE">Effective (as of today)</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        </Box>

        <ProfitLossFilters filters={filters} onChange={onChange} onReset={onReset} />

        {initialLoading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <SummaryCards summary={report.summary} />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={7}>
                <ModuleBreakdown modules={report.moduleBreakdown} />
              </Grid>
              <Grid item xs={12} md={5}>
                <ClaimSummary claim={report.claimSummary} />
              </Grid>
            </Grid>

            <ProfitLossDetailTable
              view={view}
              onViewChange={onViewChange}
              rows={page.content}
              rowCount={page.totalItems}
              pagination={pagination}
              onPaginationChange={setPagination}
              sorting={sorting}
              onSortingChange={setSorting}
              isLoading={isProfitLossLoading}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ProfitLoss;
